import React from 'react'

import { RHFInput } from 'react-hook-form-input'
import useForm from 'react-hook-form'

import { Box, Paper, Typography } from '@material-ui/core'

import { ButtonGroupForm, Button } from '@yapay/react-components'

import { useSnackbar } from 'hooks'
import { PaymentMethodCheckbox } from './../../components'
import * as service from 'service'
import constants from './../../../../../../constants'
import billet from 'images/paymentMethodsConfiguration/billet.png'
import transfer from 'images/paymentMethodsConfiguration/transfer.png'
import creditCard from 'images/paymentMethodsConfiguration/creditCard.png'
import balance from 'images/paymentMethodsConfiguration/balance.png'
import PaymentMethodsContext from 'contexts/PaymentMethodsContext'

const PaymentMethodsEdit = () => {
  const snackbar = useSnackbar()
  const { register, setValue, getValues, handleSubmit } = useForm()
  const [edit, setEdit] = React.useState(false)
  const [currentValues, setCurrentValues] = React.useState(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const context = React.useContext(PaymentMethodsContext)

  React.useEffect(() => {
    const loadPaymentMethods = async () => {
      try {
        const [
          responseBilletGateway,
          responseBillet,
          responseCreditCard,
          responseTransfer,
          responseBalance,
        ] = await Promise.all([
          service.recurrence.processors.get({
            processor: 'yapay',
            type: 'b',
          }),
          service.recurrence.settings.get({
            settingId: constants.settings.BILLET_METHOD,
          }),
          service.recurrence.settings.get({
            settingId: constants.settings.CARD_METHOD,
          }),
          service.recurrence.settings.get({
            settingId: constants.settings.TRANSFER_METHOD,
          }),
          service.recurrence.settings.get({
            settingId: constants.settings.BALANCE_METHOD,
          }),
        ])

        const billetConfigGateway = responseBilletGateway.data.paymentMethods.find(
          paymentMethod => paymentMethod.name === 'itau_billet',
        )

        setValue(
          'billet',
          !!billetConfigGateway?.status &&
            responseBillet.data.setting.value === 'yapay',
        )
        setValue(
          'creditCard',
          responseCreditCard.data.setting.value === 'yapay',
        )
        setValue('transfer', responseTransfer.data.setting.value === 'yapay')
        setValue('balance', responseBalance.data.setting.value === 'yapay')
        context.setError(false)
      } catch (error) {
        context.setError(true)
        snackbar.open({
          message:
            'Erro ao carregar as formas de pagamento! Atualize a página e tente novamente, caso persista entre em contato com o suporte.',
          variant: 'error',
          autoHideDuration: 12000,
        })
      } finally {
        context.setIsLoadingPaymentMethods(false)
      }
    }

    loadPaymentMethods()
    // eslint-disable-next-line
  }, [setValue])

  const handleClickEdit = () => {
    setCurrentValues({ ...getValues() })
    setEdit(true)
  }

  const handleClickCancel = () => {
    Object.keys(currentValues).forEach(key => setValue(key, currentValues[key]))
    setEdit(false)
  }

  const onSubmit = async data => {
    try {
      setIsLoading(true)

      const responseIntegrationToken = await service.recurrence.settings.get({
        settingId: constants.settings.YAPAY_TOKEN_ACCOUNT,
      })

      const integrationToken = responseIntegrationToken.data.setting.value

      await Promise.all([
        ...(data.billet
          ? [
              service.recurrence.settings.update({
                settingId: constants.settings.BILLET_METHOD,
                value: 'yapay',
              }),
              service.recurrence.processors.update({
                processor: 'yapay',
                methods: ['itau_billet'],
                tags: [
                  {
                    tag: 'token_account',
                    value: integrationToken,
                  },
                ],
              }),
            ]
          : [
              service.recurrence.settings.update({
                settingId: constants.settings.BILLET_METHOD,
                value: '',
              }),
              service.recurrence.processors.inactivate({
                processor: 'yapay',
                method: 'itau_billet',
              }),
            ]),
        service.recurrence.settings.update({
          settingId: constants.settings.CARD_METHOD,
          value: data.creditCard ? 'yapay' : '',
        }),
        service.recurrence.settings.update({
          settingId: constants.settings.TRANSFER_METHOD,
          value: data.transfer ? 'yapay' : '',
        }),
        service.recurrence.settings.update({
          settingId: constants.settings.BALANCE_METHOD,
          value: data.balance ? 'yapay' : '',
        }),
      ])

      snackbar.open({
        message: 'Formas de pagamento alteradas com sucesso!',
        variant: 'success',
      })

      setEdit(false)
      setIsLoading(false)
    } catch (error) {
      snackbar.open({
        message: 'Erro ao alterar as formas de pagamento! Tente novamente!',
        variant: 'error',
      })

      setIsLoading(false)
    }
  }

  return (
    <Box component={Paper} p={{ xs: 2, sm: 4 }} mb={2}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Box mb={{ xs: 0, sm: 2 }}>
            <Typography variant="h5">Formas de pagamento</Typography>
          </Box>

          {!edit && (
            <Box display={{ xs: 'none', sm: 'block' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleClickEdit}
                isLoading={context.isLoading}
                disabled={context.isLoading || context.error}
              >
                Editar
              </Button>
            </Box>
          )}
        </Box>

        <Box
          display="grid"
          gridTemplateColumns={{
            xs: '1fr 1fr',
            sm: 'repeat(auto-fill, 130px)',
          }}
          gridGap={4}
          mb={4}
        >
          <RHFInput
            as={
              <PaymentMethodCheckbox
                id="billet"
                image={billet}
                imageAlt="Boleto"
                description="Boleto"
                disabled={!edit}
              />
            }
            name="billet"
            type="checkbox"
            register={register}
            setValue={setValue}
          />

          <RHFInput
            as={
              <PaymentMethodCheckbox
                id="transfer"
                image={transfer}
                imageAlt="Transferência bancária"
                description="Transferência bancária"
                disabled={!edit}
              />
            }
            name="transfer"
            type="checkbox"
            register={register}
            setValue={setValue}
          />

          <RHFInput
            as={
              <PaymentMethodCheckbox
                id="creditCard"
                image={creditCard}
                imageAlt="Cartão de crédito"
                description="Cartão de crédito"
                disabled={!edit}
              />
            }
            name="creditCard"
            type="checkbox"
            register={register}
            setValue={setValue}
          />

          <RHFInput
            as={
              <PaymentMethodCheckbox
                id="balance"
                image={balance}
                imageAlt="Saldo Yapay"
                description="Saldo Yapay"
                disabled={!edit}
              />
            }
            name="balance"
            type="checkbox"
            register={register}
            setValue={setValue}
          />
        </Box>

        <Box mt={2}>
          <ButtonGroupForm>
            {!edit && (
              <Box display={{ xs: 'block', sm: 'none' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleClickEdit}
                  isLoading={context.isLoading}
                  disabled={context.isLoading || context.error}
                  fullWidth
                >
                  Editar
                </Button>
              </Box>
            )}

            {edit && (
              <>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  isLoading={isLoading}
                >
                  Salvar
                </Button>

                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleClickCancel}
                >
                  Cancelar
                </Button>
              </>
            )}
          </ButtonGroupForm>
        </Box>
      </form>
    </Box>
  )
}

export default PaymentMethodsEdit
